import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, CardBody, Card } from 'reactstrap';
import { Tooltip, Box, Button, LinearProgress, SnackbarContent, Autocomplete, TextField } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Redirect } from 'react-router-dom';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import UpdateRoasterModal from './UpdateRoasterModal';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ConfirmationDialog from "src/shared/components/ConfirmationDialog";
import DutyExtensionModal from './DutyExtensionModal';
import DutyRoasterUploadModal from './DutyRoasterUploadModal';
import { showNotification } from 'src/shared/components/Notification';
import { convertToHoursAndMinutes } from 'src/utils/pnd/PndUtilities';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import Badge from "react-bootstrap/Badge";
import PageHeader from 'src/shared/components/layout/PageHeader';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const HubDetailsIndex = () => {
  const [reactTableData, setReactTableData] = useState({ tableRowsData: [] });
  const [selectedRoaster, setSelectedRoaster] = useState(null);
  const [temp, setTempData] = useState("");
  const [viewDetailPopup, setViewDetailPopup] = useState(false);
  const [isTitle, setTitle] = useState(null);
  const [isNoRecordFound, setNoRecordFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDutyApproval, setDutyApproval] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isRiderRequestedHr, setRiderRequestedHr] = useState(null);
  const [isRedirect, setIsRedirect] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [riderList, setRiderList] = useState([]);
  const [selectedRider, setSelectedRider] = useState('');

  useEffect(() => {
    getAllDutyRoaster();
    getAllRiders();
  }, []);

  const getAllRiders = async () => {
    try {
      const response = await PndRestApi.GetAllRidersDetails();
      if (response.status === 200 && response.data.status === 200) {
        setRiderList(response.data?.data || []);
      }
    } catch (error) {
      console.error('Error fetching riders:', error);
    }
  };

  const riderOptions = useMemo(() => {
    const options = [{ label: 'All Riders', value: '' }];
    const addedIds = new Set();

    (riderList || []).forEach(r => {
      const id = r.riderId || r.id;
      const name = r.riderName || r.name;
      if (id && !addedIds.has(String(id))) {
        addedIds.add(String(id));
        options.push({ label: name || `Rider #${id}`, value: id });
      }
    });

    (reactTableData.tableRowsData || []).forEach(r => {
      const id = r.riderId;
      const name = r.riderName;
      if (id && !addedIds.has(String(id))) {
        addedIds.add(String(id));
        options.push({ label: name || `Rider #${id}`, value: id });
      } else if (!id && name && !addedIds.has(name)) {
        addedIds.add(name);
        options.push({ label: name, value: name });
      }
    });

    return options;
  }, [riderList, reactTableData.tableRowsData]);

  const filteredTableData = useMemo(() => {
    const allRows = reactTableData.tableRowsData || [];
    if (!selectedRider) return allRows;
    return allRows.filter(row => {
      if (row.riderId && String(row.riderId) === String(selectedRider)) return true;
      if (row.riderName && row.riderName === selectedRider) return true;
      return false;
    });
  }, [reactTableData.tableRowsData, selectedRider]);

  useEffect(() => {
    if (filteredTableData.length > 0) {
      setSelectedRoaster(prev => {
        const found = filteredTableData.find(r => r.roasterId === prev?.roasterId);
        return found || filteredTableData[0];
      });
    } else {
      setSelectedRoaster(null);
    }
  }, [filteredTableData]);

  const getAllDutyRoaster = async () => {
    try {
      setLoading(true);
      setNoRecordFound(false);
      const response = await PndRestApi.GetAllRiderDutyRoaster();
      if (response.status === 200 && response.data.status === 200) {
        const list = response.data?.data || [];
        setReactTableData({ tableRowsData: list });
        if (list.length > 0) {
          setSelectedRoaster(prev => {
            const found = list.find(r => r.roasterId === prev?.roasterId);
            return found || list[0];
          });
        } else {
          setSelectedRoaster(null);
        }
      } else if (response.status === 200 && response.data.status === 204) {
        setNoRecordFound(true);
        setReactTableData({ tableRowsData: [] });
        setSelectedRoaster(null);
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          `Message:${response.data.message}`,
        );
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setShowConfirmation(false);
  };

  const toggleUploadModal = () => {
    setUploadModalOpen(!uploadModalOpen);
  };

  const calExtendedTime = (timeInMilliseconds) => {
    if (timeInMilliseconds) {
      const timeInMinutes = Math.floor(timeInMilliseconds / 60000);
      const totalHours = Math.floor(timeInMinutes / 60);
      const minutes = timeInMinutes % 60;
      const hours = totalHours % 24;
      const formattedHours = hours < 10 ? `0${hours}` : hours;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      return `${formattedHours}:${formattedMinutes}`;
    }
    return '';
  };

  const getDayOfWeek = (roasterDate) => {
    if (!roasterDate) return '—';
    try {
      const date = new Date(roasterDate);
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    } catch (e) {
      return '—';
    }
  };

  const edit = (roaster) => {
    if (roaster.roasterId) {
      setTempData(roaster);
      setViewDetailPopup(true);
      setTitle("Edit - Rider Duty Roster Details");
    }
  };

  const processOrder = (event) => {
    if (event.roasterId) {
      setRiderRequestedHr(calExtendedTime(event?.extendedTime));
      setShowConfirmation(false);
      setDutyApproval(true);
      setTitle("Duty Extension Approval");
    }
  };

  const DutyApproval = (roaster) => {
    if (roaster.roasterId) {
      setTempData(roaster);
      setShowConfirmation(true);
    }
  };

  const ROSTER_COLUMNS = useMemo(() => [
    {
      key: 'riderName',
      header: 'Rider Name',
      sortable: true,
    },
    {
      key: 'roasterDate',
      header: 'Roster Date',
      sortable: true,
    },
    {
      key: 'roasterStatusDesc',
      header: 'Status',
      sortable: true,
      render: (value, row) => (
        <>
          {row.roasterStatusId === 2 ? (
            <Badge bg="success">{row.roasterStatusDesc}</Badge>
          ) : row.roasterStatusId === 8 ? (
            <Badge bg="warning">{row.roasterStatusDesc}</Badge>
          ) : row.roasterStatusId === 7 ? (
            <Badge bg="danger">{row.roasterStatusDesc}</Badge>
          ) : row.roasterStatusId === 1 ? (
            <Badge bg="primary">{row.roasterStatusDesc}</Badge>
          ) : (
            <Badge bg="success">{row.roasterStatusDesc}</Badge>
          )}
        </>
      )
    }
  ], []);

  if (isRedirect) {
    return <Redirect to="/web_app/rider/duty_roaster/registration" />;
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Container fluid style={{ maxWidth: '1400px' }}>
        <PageHeader
          icon={<CalendarTodayIcon fontSize="large" />}
          title="Rider Duty Roster"
          subtitle="View and manage duty schedules, shift alignments, and extensions."
          gradient="linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)"
        />

        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

        {/* Action Controls Row */}
        <div className="d-flex justify-content-end align-items-center mb-3" style={{ gap: '10px' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={toggleUploadModal}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              backgroundColor: '#3f51b5',
              '&:hover': { backgroundColor: '#303f9f' }
            }}
          >
            Upload Excel Roster
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsRedirect(true)}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              backgroundColor: '#10b981',
              '&:hover': { backgroundColor: '#059669' },
              color: '#ffffff'
            }}
          >
            Add Roster
          </Button>
        </div>

        <Row>
          {/* Left Panel: Master List */}
          <Col md={7} lg={7}>
            {!isNoRecordFound ? (
              <RadioSelectTable
                rows={filteredTableData}
                columns={ROSTER_COLUMNS}
                rowKey="roasterId"
                selectedKey={selectedRoaster?.roasterId}
                onSelect={setSelectedRoaster}
                searchKeys={['riderName', 'roasterDate', 'roasterStatusDesc']}
                searchPlaceholder="Search by Rider name..."
                pageSizes={[5, 10, 20]}
                filterDropdown={
                  <Autocomplete
                    size="small"
                    options={riderOptions}
                    getOptionLabel={(option) => option?.label || ''}
                    value={riderOptions.find(opt => String(opt.value) === String(selectedRider)) || riderOptions[0]}
                    onChange={(event, newValue) => {
                      setSelectedRider(newValue ? newValue.value : '');
                    }}
                    isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Filter by Rider"
                        label="Filter by Rider"
                        variant="outlined"
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#ffffff',
                            height: '38px',
                            fontSize: '0.85rem'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.85rem'
                          }
                        }}
                      />
                    )}
                    sx={{ minWidth: 220 }}
                  />
                }
              />
            ) : (
              <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px', textAlign: 'center' }}>
                <p className="text-muted m-0">No roaster records found.</p>
              </Card>
            )}
          </Col>

          {/* Right Panel: Sleek Detail Card */}
          <Col md={5} lg={5}>
            <Card className="card--not-full-height" style={{
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
              minHeight: '520px',
              background: '#ffffff',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div style={{ flex: 1 }}>
                {selectedRoaster ? (
                  <>
                    {/* Header Banner */}
                    <div style={{
                      background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
                      padding: '24px 20px',
                      color: '#ffffff'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <AccountCircleIcon style={{ color: '#ffffff', fontSize: '2.5rem', opacity: 0.9 }} />
                          <div>
                            <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                              {selectedRoaster.riderName}
                            </h4>
                            <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                              Roster ID: {selectedRoaster.roasterId}
                            </span>
                          </div>
                        </div>
                        <div>
                          {selectedRoaster.roasterStatusId === 2 ? (
                            <Badge bg="success">{selectedRoaster.roasterStatusDesc}</Badge>
                          ) : selectedRoaster.roasterStatusId === 8 ? (
                            <Badge bg="warning">{selectedRoaster.roasterStatusDesc}</Badge>
                          ) : selectedRoaster.roasterStatusId === 7 ? (
                            <Badge bg="danger">{selectedRoaster.roasterStatusDesc}</Badge>
                          ) : selectedRoaster.roasterStatusId === 1 ? (
                            <Badge bg="primary">{selectedRoaster.roasterStatusDesc}</Badge>
                          ) : (
                            <Badge bg="success">{selectedRoaster.roasterStatusDesc}</Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Details Body */}
                    <CardBody style={{ padding: '20px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                        {/* Shift Details */}
                        <div>
                          <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                            Shift Details
                          </h6>
                          <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted small">Shift Name:</span>
                              <span className="fw-bold small text-dark">{selectedRoaster.shiftName || '—'}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted small">Roster Date:</span>
                              <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                                <CalendarTodayIcon style={{ fontSize: '14px', color: '#64748b' }} />
                                {selectedRoaster.roasterDate} ({getDayOfWeek(selectedRoaster.roasterDate)})
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted small">Start Time:</span>
                              <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                                <ScheduleIcon style={{ fontSize: '14px', color: '#64748b' }} />
                                {convertToHoursAndMinutes(selectedRoaster.dutyStartTime)}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted small">End Time:</span>
                              <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                                <ScheduleIcon style={{ fontSize: '14px', color: '#64748b' }} />
                                {convertToHoursAndMinutes(selectedRoaster.dutyEndTime)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Extended Time Request (If Available) */}
                        {selectedRoaster.extendedTime !== null && (
                          <div>
                            <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                              Extension Details
                            </h6>
                            <div style={{ background: '#fffbeb', borderRadius: '8px', padding: '12px 14px', border: '1px solid #fef3c7', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div className="d-flex justify-content-between">
                                <span className="text-warning-dark small fw-semibold">Requested Extension:</span>
                                <span className="fw-bold small text-warning-dark">
                                  {calExtendedTime(selectedRoaster.extendedTime) || '—'}
                                </span>
                              </div>
                              {selectedRoaster.hubApprovedHours !== null && (
                                <div className="d-flex justify-content-between">
                                  <span className="text-success small fw-semibold">Approved Hours:</span>
                                  <span className="fw-bold small text-success">
                                    {calExtendedTime(selectedRoaster.hubApprovedHours) || '—'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Action Button Section */}
                        <div className="mt-auto pt-3 d-flex flex-column gap-2">
                          {selectedRoaster.extendedTime !== null && selectedRoaster.extendedApproval == null && selectedRoaster.hubApprovedHours == null && (
                            <Button
                              variant="contained"
                              color="warning"
                              fullWidth
                              startIcon={<FactCheckIcon />}
                              onClick={() => DutyApproval(selectedRoaster)}
                              sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                py: '10px',
                              }}
                            >
                              Approve Duty Extension
                            </Button>
                          )}

                          {selectedRoaster.roasterStatusId !== 2 && selectedRoaster.roasterStatusId !== 3 && (
                            <Button
                              variant="contained"
                              color="info"
                              fullWidth
                              startIcon={<EditIcon />}
                              onClick={() => edit(selectedRoaster)}
                              sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                py: '10px',
                                backgroundColor: '#0288d1',
                                '&:hover': { backgroundColor: '#01579b' }
                              }}
                            >
                              Edit Duty Details
                            </Button>
                          )}
                        </div>

                      </div>
                    </CardBody>
                  </>
                ) : (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '380px',
                  }}>
                    <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>📅</div>
                    <h5 className="fw-bold text-muted">No Roster Selected</h5>
                    <p className="text-muted small">Select a rider's duty roster from the list to view scheduling details.</p>
                  </div>
                )}
              </div>

              {/* Warning Callout Note */}
              <div style={{ padding: '0 20px 20px 20px' }}>
                <div style={{
                    backgroundColor: '#fffbeb',
                    borderLeft: '4px solid #f59e0b',
                    color: '#b45309',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    fontSize: '0.78rem',
                    lineHeight: 1.5,
                }}>
                    <strong style={{ display: 'block', marginBottom: '4px' }}>⚠️ Important Note:</strong>
                    Once the vehicle mapping is complete, please instruct the rider to download and log in to the mobile application. The rider will receive mobile notifications only after this initial login. If a duty is assigned before they log in, they will not receive any notifications.
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>

      {viewDetailPopup && (
        <UpdateRoasterModal
          modalShow={viewDetailPopup}
          setModalShow={setViewDetailPopup}
          dataInfo={temp}
          isTitle={isTitle}
          setIsRedirect={setIsRedirect}
        />
      )}

      {isDutyApproval && (
        <DutyExtensionModal
          modalShow={isDutyApproval}
          setModalShow={setDutyApproval}
          isRiderRequestedHr={isRiderRequestedHr}
          isTitle={isTitle}
          dataInfo={temp}
        />
      )}

      <DutyRoasterUploadModal
        isOpen={uploadModalOpen}
        toggle={toggleUploadModal}
        refreshData={getAllDutyRoaster}
      />

      {showConfirmation && (
        <ConfirmationDialog
          open={showConfirmation}
          onClose={closeDialog}
          onConfirm={() => processOrder(temp)}
          title="Confirmation"
          message="Do you allow him to extend his duty?"
        />
      )}
    </Box>
  );
};

export default HubDetailsIndex;